import React from "react";
import {useAppSelector} from "../../../../../redux/store";
import {Grid, Skeleton} from "@mui/material";
import {
    UserDisabledInputsContainer,
    UserPasswordContainer
} from "../../../../../pages/user/settings/personal-info/user-inputs/styled";
import BarekamText from "../../../../../pages/components/text";
import {User} from "../../../../../pages/shared/models/user/user";
import BarekamButton, {SimpleButtons} from "../../../../../pages/components/button";
import BarekamInput from "../../../../../pages/components/input";
import Message from "../../../../../pages/components/message";
import {MessageSections} from "../../../../../pages/components/message/message";
import {BarekamMenuItem, SelectedBarekamMenuItem} from "../../../../../pages/components/select/styled/orders";
import {BarekamSelect} from "../../../../../pages/components/select/select";
import {WarehouseModel} from "../../../../../pages/shared/models/order/warehouse.model";
import {CountryModel} from "../../../../../pages/shared/models/countryModel";
import {getMessage} from "../../../../../pages/shared/helper-functions";

export const WarehouseWorkerAccount = ({userData, onChange, errors, onPasswordChange, warehouses, countries}: WarehouseWorkerAccountProps) => {
    const {messageCache, languageId} = useAppSelector(state => state.messagesReducer);

    if (userData) {
        return (
            <>
                <Grid item xs={12}>
                    <UserDisabledInputsContainer>
                        <BarekamText variant={"s"} opaque>
                            <Message messageKey={"user_settings_email"} sectionId={MessageSections.USER}/>
                        </BarekamText>

                        <BarekamText variant={"s"} fontWeight={500}>
                            {userData.email}
                        </BarekamText>
                    </UserDisabledInputsContainer>
                </Grid>

                <Grid item xs={12}>
                    <UserDisabledInputsContainer>
                        <BarekamText variant={"s"} opaque>
                            <Message messageKey={"user_settings_password"} sectionId={MessageSections.USER}/>
                        </BarekamText>

                        <UserPasswordContainer>
                            <BarekamText variant={"s"} fontWeight={500}>
                                ********
                            </BarekamText>

                            <BarekamButton type={SimpleButtons.TEXT} onClick={onPasswordChange}>
                                <BarekamText variant={'m'}>
                                    <Message messageKey={"user_settings_edit_password"} sectionId={MessageSections.USER}/>
                                </BarekamText>
                            </BarekamButton>
                        </UserPasswordContainer>
                    </UserDisabledInputsContainer>
                </Grid>

                <Grid item xs={12}>
                    <BarekamInput key={'user-settings-first-name'}
                                  customInputVariant={"standard"}
                                  placeholder={getMessage('user_settings_first_name', MessageSections.USER, messageCache, languageId)}
                                  value={userData.firstName}
                                  onChange={(value) => onChange(value.target.value, 'firstName')}
                                  type={'text'}
                                  error={!!errors['firstName']}
                                  errorMessage={errors['firstName'] && getMessage(errors['firstName'], MessageSections.USER, messageCache, languageId)}
                                  width={'100%'}
                                  required/>
                </Grid>

                <Grid item xs={12}>
                    <BarekamInput key={'user-settings-last-name'}
                                  customInputVariant={"standard"}
                                  placeholder={getMessage('user_settings_last_name', MessageSections.USER, messageCache, languageId)}
                                  value={userData.lastName}
                                  onChange={(value) => onChange(value.target.value, 'lastName')}
                                  type={'text'}
                                  error={!!errors['lastName']}
                                  errorMessage={errors['recipient'] && getMessage(errors['lastName'], MessageSections.USER, messageCache, languageId)}
                                  width={'100%'}
                                  required/>
                </Grid>

                <Grid item xs={12}>
                    <BarekamSelect
                        variant={'outlined'}
                        label={getMessage('warehouse', MessageSections.USER, messageCache, languageId)}
                        value={userData.warehouse?.warehouseId || ''}
                        onChange={(value) => onChange(value.target.value, 'warehouse')}
                        type={"standard"}
                        error={!!errors['warehouse']}
                        errorMessage={errors['recipient'] && getMessage(errors['warehouse'], MessageSections.USER, messageCache, languageId)}>
                        {warehouses.map((warehouse, index) => {
                            if (userData.warehouse?.warehouseId === warehouse.warehouseId) {
                                return (
                                    <SelectedBarekamMenuItem value={warehouse.warehouseId}
                                                             key={index}>
                                        <Message messageKey={warehouse.city} sectionId={MessageSections.MAIN}/>
                                    </SelectedBarekamMenuItem>
                                )
                            } else {
                                return (
                                    <BarekamMenuItem value={warehouse.warehouseId}
                                                     key={index}>
                                        <Message messageKey={warehouse.city} sectionId={MessageSections.MAIN}/>
                                    </BarekamMenuItem>
                                );
                            }
                        })}
                    </BarekamSelect>
                </Grid>

                <Grid item xs={12}>
                    <BarekamSelect
                        variant={'outlined'}
                        label={getMessage('location', MessageSections.USER, messageCache, languageId)}
                        value={userData.locationId?.countryId || ''}
                        onChange={(value) => onChange(value.target.value, 'locationId')}
                        type={"standard"}
                        error={!!errors['locationId']}
                        errorMessage={errors['recipient'] && getMessage(errors['locationId'], MessageSections.USER, messageCache, languageId)}>
                        {countries.map((country, index) => {
                            if (userData.locationId?.countryId === country.countryId) {
                                return (
                                    <SelectedBarekamMenuItem value={country.countryId}
                                                             key={index}>
                                        {country.name[languageId]}
                                    </SelectedBarekamMenuItem>
                                )
                            } else {
                                return (
                                    <BarekamMenuItem value={country.countryId}
                                                     key={index}>
                                        {country.name[languageId]}
                                    </BarekamMenuItem>
                                );
                            }
                        })}
                    </BarekamSelect>
                </Grid>
            </>
        )
    } else {
        return (
            <>
                <Skeleton width={'50%'} height={'5.5rem'}>
                </Skeleton>

                <Skeleton width={'50%'} height={'5.5rem'}>
                </Skeleton>

                <Skeleton width={'50%'} height={'5.5rem'}>
                </Skeleton>
            </>
        )
    }
}

interface WarehouseWorkerAccountProps {
    userData?: User,
    onChange: (value: any, field: string) => void,
    onPasswordChange: () => void,
    errors: { [key: string]: string },
    warehouses: WarehouseModel[],
    countries: CountryModel[]
}
